// src/services/notification/notificationService.ts

import { Customer } from '../../shared/types';
import { EmailService } from '../email/EmailService';
import { EmailSenderIdentity, EmailAttachment } from '../email/types';
import { resolveEffectiveAnrede } from '../email/effectiveAnrede';

export class NotificationService {
  private emailService: EmailService;

  constructor() {
    this.emailService = new EmailService();
  }

  /**
   * Sendet Lohnabrechnungen per E-Mail mit intelligenter Versandlogik
   * ERWEITERT: Jetzt mit Unified Template und vollständiger E-Mail-Vorschau
   *
   * Stufe 3 (terminierter Versand): Ist scheduledAtLocal gesetzt (Berliner
   * Wanduhrzeit) und läuft KEIN Testmodus, wird die fertig gerenderte Mail
   * eingefroren statt versendet — der Minuten-Cron email-scheduled-send
   * verschickt sie zum Zeitpunkt. erstelltVon ist dabei die Innenidentität
   * (wer die Finalisierung ausgelöst hat) für Audit und Geplant-Anzeige.
   * Testversand ist bewusst immer sofort.
   */
  async sendLohnauswertung(
    customer: Customer,
    month: number,
    year: number,
    userName: string,
    file?: File,
    isTestMode?: boolean,
    additionalFiles?: Record<string, File>,
    senderIdentity?: EmailSenderIdentity,
    customMessage?: string,
    scheduledAtLocal?: string | null,
    erstelltVon?: { name: string; email: string }
  ): Promise<boolean> {
    try {
      // Validierung
      if (!customer.email) {
        console.error('Keine E-Mail-Adresse für Kunden vorhanden');
        return false;
      }

      // Keine E-Mail bei "kein_versand" (außer im Testmodus)
      if (customer.versandmethode === 'kein_versand' && !isTestMode) {
        console.log('Versandmethode ist "kein_versand", keine E-Mail wird versendet');
        return true;
      }

      if (!customer.mandantennummer && customer.versandmethode !== 'duo' && !isTestMode) {
        console.error('Kunde hat keine Mandantennummer');
        return false;
      }

      const monthNames = [
        'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
        'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
      ];

      // Bereite Attachments vor
      let attachments: EmailAttachment[] = [];
      let individualAttachmentNames: string[] = [];
      
      // Hauptdatei nur bei NICHT-DUO hinzufügen
      if (customer.versandmethode !== 'duo') {
        // Standard E-Mail mit Anhang
        if (!file && customer.versandmethode === 'email' && !isTestMode) {
          console.error('Keine Datei für E-Mail-Versand vorhanden');
          return false;
        }

        if (file) {
          const attachment = await this.emailService.fileToBase64(file);
          attachments = [attachment];
        }
      }

      // Individuelle Anhänge IMMER hinzufügen (außer bei kein_versand)
      // Diese werden sowohl bei E-Mail als auch bei DUO angehängt
      if (additionalFiles && Object.keys(additionalFiles).length > 0) {
        const additionalAttachments = await Promise.all(
          Object.values(additionalFiles).map(async file => {
            // Sammle die Dateinamen für das E-Mail-Template
            individualAttachmentNames.push(file.name);
            return await this.emailService.fileToBase64(file);
          })
        );
        attachments = [...attachments, ...additionalAttachments];
      }

      // E-Mail-Variablen vorbereiten - jetzt mit Attachment-Informationen
      const emailVariables = {
        customerName: customer.company,
        customerNumber: customer.mandantennummer || 'TEST',
        month: monthNames[month - 1],
        monthNumber: month,
        year,
        userName: userName,
        nachname: customer.nachname || undefined,
        vorname: customer.vorname || undefined,
        anrede: resolveEffectiveAnrede(customer.anrede, senderIdentity?.id, customer.duz_mitarbeiter_ids) as 'Du' | 'Sie' | undefined,
        geschlecht: customer.geschlecht as 'männlich' | 'weiblich' | 'divers' | undefined,
        testPrefix: isTestMode ? '[TEST] ' : '',
        senderIdentity: senderIdentity,
        imAuftragVon: customer.im_auftrag_von || undefined,
        customMessage: customMessage || undefined,
        isDuoNotification: customer.versandmethode === 'duo', // NEU: Flag für DUO
        customerEmail: customer.email, // NEU: Für Preview
        customerEmailCc: customer.email_cc || undefined, // NEU: CC-Empfänger
        // NEU: Information über individuelle Anhänge
        individualAttachments: individualAttachmentNames.length > 0 ? individualAttachmentNames : undefined
      };

      // Testversand: Überschreibe Empfänger
      const recipient = isTestMode ? 'test@xtax.de' : customer.email;

      // BCC-Logik: Nur bei Realversand den Sender auf BCC setzen
      const bccRecipient = (!isTestMode && senderIdentity?.email) ? senderIdentity.email : undefined;

      // CC-Logik: Nur bei Realversand CC-Empfänger hinzufügen
      const ccRecipients = (!isTestMode && customer.email_cc) ? customer.email_cc : undefined;

      // Verwende immer das unified Template
      const templateId = 'unified-email';

      // E-Mail senden mit unified Template
      // Stufe 3: Terminierung nur im Realversand durchreichen — der Testversand
      // ist immer sofort. Die Innenidentität wandert als scheduleContext mit
      // (Persistierung + Geplant-Anzeige); fehlt sie trotz Terminierung, lehnt
      // der EmailService den Request mit klarer Fehlermeldung ab.
      const result = await this.emailService.sendEmail({
        to: recipient,
        templateId: templateId,
        variables: emailVariables,
        attachments: attachments,
        senderIdentity: senderIdentity,
        bcc: bccRecipient,
        cc: ccRecipients, // NEU: CC hinzufügen
        ...(!isTestMode && scheduledAtLocal
          ? {
              scheduledAtLocal,
              ...(erstelltVon
                ? {
                    scheduleContext: {
                      customerId: customer.id,
                      monat: month,
                      jahr: year,
                      erstelltVonName: erstelltVon.name,
                      erstelltVonEmail: erstelltVon.email
                    }
                  }
                : {})
            }
          : {})
      });

      return result.success;
    } catch (error) {
      console.error('Error in sendLohnauswertung:', error);
      return false;
    }
  }

  /**
   * Generiert eine E-Mail-Vorschau für die UI
   * NEU: Nutzt den EmailPreviewService für vollständige Vorschau
   */
  async generateEmailPreview(
    customer: Customer,
    month: number,
    year: number,
    senderIdentity?: EmailSenderIdentity,
    customMessage?: string,
    attachments?: Array<{ filename: string; size?: number }>
  ) {
    const { emailPreviewService } = await import('../email/EmailPreviewService');
    
    const monthNames = [
      'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
      'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
    ];

    const emailVariables = {
      customerName: customer.company,
      customerNumber: customer.mandantennummer || 'TEST',
      month: monthNames[month - 1],
      monthNumber: month,
      year,
      nachname: customer.nachname || undefined,
      vorname: customer.vorname || undefined,
      anrede: resolveEffectiveAnrede(customer.anrede, senderIdentity?.id, customer.duz_mitarbeiter_ids) as 'Du' | 'Sie' | undefined,
      geschlecht: customer.geschlecht as 'männlich' | 'weiblich' | 'divers' | undefined,
      senderIdentity: senderIdentity,
      imAuftragVon: customer.im_auftrag_von || undefined,
      customMessage: customMessage || undefined,
      isDuoNotification: customer.versandmethode === 'duo',
      customerEmail: customer.email,
      customerEmailCc: customer.email_cc || undefined, // NEU: CC in Preview
      // NEU: Attachment-Information für Preview
      individualAttachments: attachments?.map(a => a.filename)
    };

    return emailPreviewService.generatePreview(emailVariables, attachments);
  }
}

// Singleton instance
export const notificationService = new NotificationService();
